import type { InfiniteData } from '@tanstack/react-query';
import { LLMMessageRole } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { makeObservable, observable, runInAction } from 'mobx';

import { TreadAgentConversation, TreadAgentMessage } from '~hooks/useTreadAgent/models';
import { queryClient } from '~queries/queryClient';
import {
  TREAD_AGENT_QUERY_KEY,
  TREAD_AGENT_SKILLS_QUERY_KEY,
  treadAgentConversationQueryKey,
} from '~queries/treadAgentQueryKeys';
import type { TreadAgentMessagesPage } from '~queries/useTreadAgentMessagesInfiniteQuery';
import { realTimeChannels } from '~services/realTimeChannels';
import {
  createEmptyTurn,
  createStreamingTurn,
  reduceActiveTurn,
} from '~tread-agent/treadAgentConversationReducer';
import {
  ActiveTurn,
  CompletedTurnTrace,
  ResolvedToolCall,
  TreadAgentChannelEvent,
} from '~tread-agent/types';
import {
  subscribeWithMultipleHandlers,
  unsubscribeHandler,
} from '~utils/rtu/rtu-multi-handler';

const TREAD_AGENT_RTU_HANDLER_KEY = 'tread-agent-store';

// Update-or-unshift a message into the transcript's infinite-query cache. Newest-first, so a
// new message heads the first page; an already-present id is replaced so it never renders twice.
function upsertMessageIntoCache(
  existing: InfiniteData<TreadAgentMessagesPage> | undefined,
  message: TreadAgentMessage,
): InfiniteData<TreadAgentMessagesPage> {
  if (!existing || existing.pages.length === 0) {
    return {
      pageParams: [undefined],
      pages: [{ messages: [message], nextCursor: undefined }],
    };
  }

  const alreadyPresent = existing.pages.some((page) =>
    page.messages.some((existingMessage) => existingMessage.id === message.id),
  );

  if (alreadyPresent) {
    return {
      ...existing,
      pages: existing.pages.map((page) => ({
        ...page,
        messages: page.messages.map((existingMessage) =>
          existingMessage.id === message.id ? message : existingMessage,
        ),
      })),
    };
  }

  const [firstPage, ...remainingPages] = existing.pages;

  return {
    ...existing,
    pages: [
      { ...firstPage, messages: [message, ...firstPage.messages] },
      ...remainingPages,
    ],
  };
}

/**
 * Owns the *live* Tread Agent turn for the currently-open conversation: the streamed reply text
 * (via the pure reducer) and the ActionCable subscription. Durable state — the conversation list
 * and persisted history — lives in React Query (see ~queries/useTreadAgent*Query). A sent or
 * completed message is folded straight into the transcript cache so the hand-off from live turn
 * to durable history is synchronous, then invalidated to reconcile with the server (which brings
 * the message's write-action cards). Imperative API requests (create / send / confirm / cancel)
 * live in useTreadAgent, which calls the generated client and drives this store — no API calls
 * happen here.
 */
export default class TreadAgentStore {
  activeConversationId: string | null = null;
  activeTurn: ActiveTurn = createEmptyTurn();
  // The live turn is dropped as soon as its message is persisted, so a finished turn's trace has
  // to outlive it here. In-session only; nothing is stored server-side.
  completedTurnTraces: Record<string, CompletedTurnTrace> = {};
  // React key shared by the live turn and the message it persists as.
  turnKey = crypto.randomUUID();
  turnKeyByMessageId: Record<string, string> = {};
  // Public only because MobX cannot annotate a private field. Read through elapsedTurnMs.
  turnStartedAtMs: number | null = null;
  turnEndedAtMs: number | null = null;
  pendingPrompt: string | null = null;
  lastResolvedToolCall: ResolvedToolCall | null = null;

  private subscribedConversationId: string | null = null;
  // Company scope of the active conversation, needed to key its transcript cache.
  private activeCompanyId: string | null = null;

  constructor() {
    makeObservable(this, {
      activeConversationId: observable,
      activeTurn: observable.ref,
      completedTurnTraces: observable.shallow,
      turnKey: observable,
      turnKeyByMessageId: observable.ref,
      turnEndedAtMs: observable,
      turnStartedAtMs: observable,
      pendingPrompt: observable,
      lastResolvedToolCall: observable.ref,
    });
  }

  get isTurnInProgress() {
    return this.activeTurn.status === 'streaming';
  }

  get isStopRequested() {
    return this.activeTurn.isStopRequested;
  }

  markStopRequested() {
    if (!this.isTurnInProgress) {
      return;
    }

    runInAction(() => {
      this.activeTurn = { ...this.activeTurn, isStopRequested: true };
    });
  }

  // The turn is still running, so a failed stop request has to stay retryable.
  clearStopRequest() {
    runInAction(() => {
      this.activeTurn = { ...this.activeTurn, isStopRequested: false };
    });
  }

  elapsedTurnMs(nowMs: number) {
    if (this.turnStartedAtMs === null) {
      return 0;
    }

    // nowMs only ticks while a turn runs, so it is stale when the next one opens — without the
    // clamp the first frame subtracts a fresh start from an old now and paints a negative label.
    return Math.max(0, (this.turnEndedAtMs ?? nowMs) - this.turnStartedAtMs);
  }

  turnKeyForMessage(messageId: string) {
    return this.turnKeyByMessageId[messageId] ?? messageId;
  }

  traceForMessage(messageId: string) {
    return this.completedTurnTraces[messageId] ?? null;
  }

  // Opens a streaming turn so the working state shows before turn_started — unless the channel
  // already began this turn (raced ahead of the 202) or the user switched away. Called by
  // useTreadAgent right after the message POST.
  beginStreamingTurn(conversationId: string) {
    const canOpenTurn =
      this.activeConversationId === conversationId &&
      (this.activeTurn.status === 'idle' || this.activeTurn.status === 'error');

    if (!canOpenTurn) {
      return;
    }

    this.markTurnStarted();

    runInAction(() => {
      this.activeTurn = createStreamingTurn();
    });
  }

  // Points the store (subscription + live turn) at a conversation. Idempotent, so a page
  // route change and the store both settling on the same id is a no-op. History is loaded
  // by React Query, not here.
  openConversation(companyId: string, conversationId: string) {
    if (this.activeConversationId === conversationId) {
      return;
    }

    this.setActiveConversation(companyId, conversationId);
  }

  requestPrompt(prompt: string) {
    this.closeConversation();

    runInAction(() => {
      this.pendingPrompt = prompt;
    });
  }

  clearPendingPrompt() {
    runInAction(() => {
      this.pendingPrompt = null;
    });
  }

  closeConversation() {
    this.unsubscribeFromConversation();
    this.activeCompanyId = null;
    this.clearTurnClock();

    runInAction(() => {
      this.activeConversationId = null;
      this.activeTurn = createEmptyTurn();
      this.completedTurnTraces = {};
      this.turnKeyByMessageId = {};
    });
  }

  receiveChannelEvent(event: TreadAgentChannelEvent) {
    if (
      !this.activeConversationId ||
      event.conversation_id !== this.activeConversationId
    ) {
      return;
    }

    const wasStreaming = this.isTurnInProgress;

    runInAction(() => {
      this.activeTurn = reduceActiveTurn(this.activeTurn, event);
    });

    if (!wasStreaming && this.isTurnInProgress) {
      this.markTurnStarted();
    }

    const turnEnded = event.type === 'turn_done' || event.type === 'turn_cancelled';

    if (turnEnded || event.type === 'turn_error') {
      this.markTurnEnded();
    }

    if (turnEnded) {
      this.storeCompletedTrace(event.final_message_id);
      this.storeTurnKey(event.final_message_id);
    }

    // Promote the finished reply text to a durable transcript message now, so the view hands
    // off from the live turn synchronously instead of during the invalidation's refetch — the
    // gap that let a follow-up send wipe the still-showing reply. The refetch reconciles by id
    // and supplies the message's write-action cards.
    if (turnEnded && event.final_message_id && this.activeCompanyId) {
      const { text: assistantText, visualizations } = this.activeTurn;

      if (assistantText || visualizations.length > 0) {
        this.writeMessageToCache(
          this.activeCompanyId,
          event.conversation_id,
          new TreadAgentMessage(
            event.final_message_id,
            LLMMessageRole.ASSISTANT,
            assistantText || null,
            dayjs(),
            [],
            // A streamed assistant message never carries user file attachments.
            [],
            visualizations,
          ),
        );
      }
    }

    if (event.type === 'tool_call_resolved') {
      runInAction(() => {
        this.lastResolvedToolCall = {
          ...event,
          preview: this.findToolCall(event.tool_call_id)?.preview ?? null,
        };
      });
    }

    // A resolved tool call is no longer pending on the server; a turn that ended — completed or
    // stopped — adds a persisted message. Refetch the durable record.
    if (event.type === 'tool_call_resolved' || turnEnded) {
      this.invalidateConversationQueries();
    }

    if (event.type === 'tool_call_resolved' && this.activeCompanyId) {
      queryClient.invalidateQueries({
        queryKey: [TREAD_AGENT_SKILLS_QUERY_KEY, this.activeCompanyId],
        exact: true,
      });
    }

    if (event.type === 'conversation_title_updated') {
      this.invalidateConversationRecordQueries();
    }
  }

  private setActiveConversation(companyId: string, conversationId: string) {
    this.activeCompanyId = companyId;
    this.clearTurnClock();

    runInAction(() => {
      this.activeConversationId = conversationId;
      this.activeTurn = createEmptyTurn();
      this.completedTurnTraces = {};
      this.turnKeyByMessageId = {};
    });
    this.subscribeToConversation(conversationId);
  }

  // Runs before this turn's own message is folded into the cache, so a hit means the message
  // came from an earlier turn or from loaded history.
  private isMessageInTranscript(messageId: string) {
    return this.transcriptMessages().some((message) => message.id === messageId);
  }

  // A pending card whose transcript page is not loaded yet exists only on the conversation record.
  private findToolCall(toolCallId: string) {
    const conversation = queryClient.getQueryData<TreadAgentConversation | null>(
      treadAgentConversationQueryKey(this.activeCompanyId, this.activeConversationId),
    );

    return [
      ...this.transcriptMessages().flatMap((message) => message.toolCalls),
      ...(conversation?.pendingToolCalls ?? []),
    ].find((toolCall) => toolCall.id === toolCallId);
  }

  private transcriptMessages() {
    if (!this.activeCompanyId || !this.activeConversationId) {
      return [];
    }

    const transcript = queryClient.getQueryData<InfiniteData<TreadAgentMessagesPage>>([
      TREAD_AGENT_QUERY_KEY,
      'messages',
      this.activeCompanyId,
      this.activeConversationId,
    ]);

    return transcript?.pages.flatMap((page) => page.messages) ?? [];
  }

  private markTurnStarted() {
    runInAction(() => {
      this.turnKey = crypto.randomUUID();
      this.turnStartedAtMs = Date.now();
      this.turnEndedAtMs = null;
    });
  }

  private markTurnEnded() {
    runInAction(() => {
      this.turnEndedAtMs = Date.now();
    });
  }

  private clearTurnClock() {
    runInAction(() => {
      this.turnStartedAtMs = null;
      this.turnEndedAtMs = null;
    });
  }

  // Same guard as storeCompletedTrace: an id already in the transcript is an earlier turn's.
  private storeTurnKey(finalMessageId: string | null) {
    // A key already handed to an earlier message means no turn started since, so reusing it
    // would give two messages the same React key.
    const turnKeyAlreadyStored = Object.values(this.turnKeyByMessageId).includes(
      this.turnKey,
    );

    if (
      !finalMessageId ||
      turnKeyAlreadyStored ||
      this.isMessageInTranscript(finalMessageId)
    ) {
      return;
    }

    runInAction(() => {
      this.turnKeyByMessageId = {
        ...this.turnKeyByMessageId,
        [finalMessageId]: this.turnKey,
      };
    });
  }

  private storeCompletedTrace(finalMessageId: string | null) {
    const { trace } = this.activeTurn;

    if (!finalMessageId || trace.length === 0) {
      return;
    }

    // final_message_id is the chat's newest assistant message, which on a turn that persisted no
    // message of its own is an earlier turn's. Anything already in the transcript predates this
    // turn, so attaching to it would show this turn's work under an older answer.
    if (this.isMessageInTranscript(finalMessageId)) {
      return;
    }

    const elapsedSeconds = this.elapsedTurnMs(Date.now()) / 1000;

    runInAction(() => {
      this.completedTurnTraces = {
        ...this.completedTurnTraces,
        [finalMessageId]: { trace, elapsedSeconds },
      };
    });
  }

  private invalidateConversationQueries() {
    queryClient.invalidateQueries({ queryKey: [TREAD_AGENT_QUERY_KEY] });
  }

  // Refetches the conversation record and the conversation list, but deliberately NOT the
  // transcript: a retitle changes no message, and refetching mid-turn would race the optimistic
  // cache writes this store and useTreadAgent make around a send.
  private invalidateConversationRecordQueries() {
    queryClient.invalidateQueries({ queryKey: [TREAD_AGENT_QUERY_KEY, 'conversation'] });
    queryClient.invalidateQueries({ queryKey: [TREAD_AGENT_QUERY_KEY, 'conversations'] });
  }

  // Folds a message into the transcript cache so it becomes durable immediately, without a
  // refetch (mirrors the ProjectsQueryContext / Files.tsx cache writes). Called by the store's
  // own turn-completion fold and by useTreadAgent for the optimistic just-sent message.
  writeMessageToCache(
    companyId: string,
    conversationId: string,
    message: TreadAgentMessage,
  ) {
    queryClient.setQueryData<InfiniteData<TreadAgentMessagesPage>>(
      [TREAD_AGENT_QUERY_KEY, 'messages', companyId, conversationId],
      (existing) => upsertMessageIntoCache(existing, message),
    );
  }

  private subscribeToConversation(conversationId: string) {
    if (this.subscribedConversationId === conversationId) {
      return;
    }

    this.unsubscribeFromConversation();
    subscribeWithMultipleHandlers<TreadAgentChannelEvent>(
      realTimeChannels.UserTreadAgentChannel,
      null,
      TREAD_AGENT_RTU_HANDLER_KEY,
      (event) => this.receiveChannelEvent(event),
      { conversation_id: conversationId },
    );
    this.subscribedConversationId = conversationId;
  }

  private unsubscribeFromConversation() {
    if (this.subscribedConversationId) {
      // The subscription is keyed by conversation_id (see subscribeToConversation), so the
      // same scope must be passed here or unsubscribeHandler targets the wrong key and the
      // cable subscription leaks — one per conversation ever opened, including on every
      // company switch.
      unsubscribeHandler(
        realTimeChannels.UserTreadAgentChannel,
        null,
        TREAD_AGENT_RTU_HANDLER_KEY,
        { conversation_id: this.subscribedConversationId },
      );
      this.subscribedConversationId = null;
    }
  }
}
